import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    getFunnels,
    createFunnel,
    updateFunnel,
    deleteFunnel,
    activateFunnel,
    deactivateFunnel,
    getMetaPages,
} from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import {
    FunnelHeader,
    FunnelStats,
    FunnelFiltersComponent,
    FunnelGrid,
    CreateFunnelModal,
    EditFunnelModal,
    DuplicateFunnelModal,
    ExportImportModal,
} from './components';
import { Funnel, FunnelFilters, FunnelFormData, FunnelGroupCreateResponse } from '@/types/funnel';
import { sortFunnels, filterFunnels } from './utils/funnelUtils';
import VariablesCopyDialog from '@/pages/Meta/Broadcast/components/VariablesCopyDialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import type { PageVariablesValidation, Page } from '@/types/broadcast';

type PageOption = { id: string; name: string };

export default function Funnels() {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
    const [isExportImportOpen, setIsExportImportOpen] = useState(false);
    const [exportFunnelTarget, setExportFunnelTarget] = useState<Funnel | null>(null);
    const [selectedFunnel, setSelectedFunnel] = useState<Funnel | null>(null);
    const [variablesCopyOpen, setVariablesCopyOpen] = useState(false);
    const [pagesWithMissing, setPagesWithMissing] = useState<PageVariablesValidation[]>([]);
    const [pendingActivateFunnel, setPendingActivateFunnel] = useState<Funnel | null>(null);
    const [confirmToggle, setConfirmToggle] = useState<Funnel | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<Funnel | null>(null);
    const [confirmAfterFixed, setConfirmAfterFixed] = useState<Funnel | null>(null);
    const [filters, setFilters] = useState<FunnelFilters>({
        sortBy: 'created_at',
        sortOrder: 'desc'
    });

    const { toast } = useToast();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // Query para listar funis (refetch a cada 5s se há tradução em andamento)
    const { data: funnelsData, isLoading, error } = useQuery({
        queryKey: ['funnels'],
        // @ts-ignore
        queryFn: getFunnels,
        refetchInterval: (query) => {
            const funnels = query.state.data as any[] | undefined;
            const hasRunning = funnels?.some((f: any) => {
                const s = f.translation_status?.status;
                return s === 'running' || s === 'queued';
            });
            return hasRunning ? 5000 : false;
        },
    });

    // Query para meta pages (mapa page_id -> id interno)
    const { data: metaPagesData } = useQuery({
        queryKey: ['meta-pages'],
        queryFn: getMetaPages,
        staleTime: 1000 * 60,
    });

    const pageIdToInternalId = useMemo(() => {
        const map = new Map<string, number>();
        if (metaPagesData) {
            for (const p of metaPagesData) {
                map.set(p.page_id, p.id);
            }
        }
        return map;
    }, [metaPagesData]);

    const funnels = useMemo((): Funnel[] => {
        if (!funnelsData) return [];

        // @ts-ignore
        return funnelsData.map((response: FunnelResponse) => ({
            id: response.id,
            user_id: response.user_id,
            nome: response.nome,
            descricao: response.descricao,
            utm_source: response.utm_source,
            utm_medium: response.utm_medium,
            utm_campaign: response.utm_campaign,
            is_active: response.is_active,
            ai_translate: response.ai_translate,
            ai_provider: response.ai_provider,
            total_nodes: (response as any).total_nodes,
            pages: response.pages ?? [],
            created_by_name: (response as any).created_by_name ?? undefined,
            created_at: response.created_at,
            updated_at: response.updated_at,
        }));
    }, [funnelsData]);

    const pages = useMemo<PageOption[]>(() => {
        if (!funnelsData) return [];

        const unique = new Map<string, PageOption>();

        // @ts-ignore
        for (const f of funnelsData as FunnelResponse[]) {
            for (const p of f.pages ?? []) {
                if (!unique.has(p.page_id)) {
                    unique.set(p.page_id, { id: p.page_id, name: p.page_name || p.page_id });
                }
            }
        }

        return Array.from(unique.values()).sort((a, b) =>
            a.name.localeCompare(b.name, 'pt-BR')
        );
    }, [funnelsData]);

    // Colaboradores com funis visíveis (só vem preenchido pro partner)
    const creators = useMemo(() => {
        const map = new Map<string, string>();
        funnels.forEach(f => {
            if (f.created_by_name) map.set(String(f.user_id), f.created_by_name);
        });
        return Array.from(map, ([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    }, [funnels]);

    // Filtrar e ordenar funis
    const filteredAndSortedFunnels = useMemo(() => {
        let result = filterFunnels(funnels, filters.page, filters.status);
        if (filters.createdBy === 'mine') {
            result = result.filter(f => !f.created_by_name);
        } else if (filters.createdBy) {
            result = result.filter(f => String(f.user_id) === filters.createdBy);
        }
        result = sortFunnels(result, filters.sortBy || 'created_at', filters.sortOrder || 'desc');
        return result;
    }, [funnels, filters]);

    // Calcular estatísticas
    const stats = useMemo(() => {
        const activeFunnels = funnels.filter(f => f.is_active).length;
        const inactiveFunnels = funnels.filter(f => !f.is_active).length;

        return {
            totalFunnels: funnels.length,
            activeFunnels,
            inactiveFunnels
        };
    }, [funnels]);

    // Mutation para criar funil
    const createFunnelMutation = useMutation({
        mutationFn: createFunnel,
        onSuccess: (data) => {
            const nome = data.funnel?.nome ?? 'Funil';
            const pageCount = data.funnel?.pages?.length ?? 0;
            toast({
                title: 'Funil criado!',
                description: `"${nome}" vinculado a ${pageCount} página${pageCount !== 1 ? 's' : ''}.`,
            });
            queryClient.invalidateQueries({ queryKey: ['funnels'] });
            setIsCreateModalOpen(false);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar funil',
                description: error.response?.data?.detail || 'Ocorreu um erro ao criar o funil.',
            });
        },
    });

    // Mutation para atualizar funil
    const updateFunnelMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) => updateFunnel(id, data),
        onSuccess: () => {
            toast({
                title: 'Funil atualizado!',
                description: 'O funil foi atualizado com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnels'] });
            setIsEditModalOpen(false);
            setSelectedFunnel(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar funil',
                description: error.response?.data?.detail || 'Ocorreu um erro ao atualizar o funil.',
            });
        },
    });

    // Mutation para deletar funil
    const deleteFunnelMutation = useMutation({
        mutationFn: deleteFunnel,
        onSuccess: () => {
            toast({
                title: 'Funil excluído!',
                description: 'O funil foi excluído com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnels'] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao excluir funil',
                description: error.response?.data?.detail || 'Ocorreu um erro ao excluir o funil.',
            });
        },
    });

    // Mutation para ativar/desativar funil
    const toggleActiveMutation = useMutation({
        mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
            isActive ? deactivateFunnel(id) : activateFunnel(id),
        onSuccess: (_, variables) => {
            toast({
                title: variables.isActive ? 'Funil desativado!' : 'Funil ativado!',
                description: `O funil foi ${variables.isActive ? 'desativado' : 'ativado'} com sucesso.`,
            });
            queryClient.invalidateQueries({ queryKey: ['funnels'] });
        },
        onError: (error: any, variables) => {
            const errorDetail = error.response?.data?.detail;

            // Se tiver páginas com variáveis faltando, abre o modal de correção
            if (!variables.isActive && errorDetail?.pages_with_missing?.length > 0 && pendingActivateFunnel) {
                setPagesWithMissing(errorDetail.pages_with_missing);
                setVariablesCopyOpen(true);
                return;
            }

            let errorMessage = `Ocorreu um erro ao ${variables.isActive ? 'desativar' : 'ativar'} o funil.`;
            if (errorDetail?.message) errorMessage = errorDetail.message;
            if (errorDetail?.errors && Array.isArray(errorDetail.errors)) {
                errorMessage = errorDetail.errors.join('\n');
            }

            toast({
                variant: 'destructive',
                title: `Erro ao ${variables.isActive ? 'desativar' : 'ativar'} funil`,
                description: errorMessage,
            });
        },
    });

    // Mutation para duplicar funil (usa create com source_funnel_id)
    const duplicateFunnelMutation = useMutation({
        mutationFn: (pageIds: number[]) => createFunnel({
            nome: `${selectedFunnel?.nome} (cópia)`,
            page_ids: pageIds,
            source_funnel_id: selectedFunnel?.id,
        }),
        onSuccess: (data: FunnelGroupCreateResponse) => {
            const nome = data?.funnel?.nome ?? 'Funil';
            toast({
                title: 'Funil duplicado!',
                description: `O funil "${nome}" foi criado com sucesso.`,
            });
            setIsDuplicateModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['funnels'] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao duplicar funil',
                description: error.response?.data?.detail || 'Ocorreu um erro ao duplicar o funil.',
            });
        },
    });

    // Handlers
    const handleCreateFunnel = (data: FunnelFormData) => {
        createFunnelMutation.mutate(data);
    };

    const handleView = (funnel: Funnel) => {
        navigate(`/meta/flow/${funnel.id}`);
    };

    const handleEdit = (funnel: Funnel) => {
        setSelectedFunnel(funnel);
        setIsEditModalOpen(true);
    };

    const handleEditSubmit = (data: { nome?: string; descricao?: string; utm_source?: string; utm_medium?: string; utm_campaign?: string }) => {
        if (!selectedFunnel) return;
        updateFunnelMutation.mutate({ id: selectedFunnel.id, data });
    };

    const handleDelete = (funnel: Funnel) => {
        setConfirmDelete(funnel);
    };

    const handleDuplicate = (funnel: Funnel) => {
        setSelectedFunnel(funnel);
        setIsDuplicateModalOpen(true);
    };

    const handleExport = (funnel: Funnel) => {
        setExportFunnelTarget(funnel);
        setIsExportImportOpen(true);
    };

    const handleImportOpen = () => {
        setExportFunnelTarget(null);
        setIsExportImportOpen(true);
    };

    const handleToggleActive = (funnel: Funnel) => {
        setConfirmToggle(funnel);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <FunnelHeader onCreateClick={() => setIsCreateModalOpen(true)} onImportClick={handleImportOpen} />

            {/* Loading State */}
            {isLoading && (
                <div className="rounded-2xl bg-white p-8 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0f172a]"></div>
                        <p className="ml-3 text-gray-500">Carregando funis...</p>
                    </div>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className="rounded-2xl bg-red-50 border border-red-200/50 p-6">
                    <p className="text-red-700 font-medium">
                        Erro ao carregar funis. Tente novamente.
                    </p>
                </div>
            )}

            {/* Stats */}
            {!isLoading && !error && funnels.length > 0 && (
                <FunnelStats
                    totalFunnels={stats.totalFunnels}
                    activeFunnels={stats.activeFunnels}
                    inactiveFunnels={stats.inactiveFunnels}
                />
            )}

            {/* Filtros */}
            {!isLoading && !error && funnels.length > 0 && (
                <FunnelFiltersComponent
                    pages={pages}
                    creators={creators}
                    filters={filters}
                    onFiltersChange={setFilters}
                />
            )}

            {/* Grid de Funis */}
            {!isLoading && !error && filteredAndSortedFunnels.length > 0 ? (
                <FunnelGrid
                    funnels={filteredAndSortedFunnels}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleActive={handleToggleActive}
                    onDuplicate={handleDuplicate}
                    onExport={handleExport}
                    pageIdToInternalId={pageIdToInternalId}
                />
            ) : !isLoading && !error && funnels.length > 0 ? (
                <div className="rounded-2xl bg-white p-12 shadow-sm border border-gray-100 text-center">
                    <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#7dd3fc]/20 to-[#38bdf8]/10 rounded-2xl flex items-center justify-center mb-4">
                        <svg
                            className="w-8 h-8 text-[#0f172a]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Nenhum funil encontrado
                    </h3>
                    <p className="text-gray-600 mb-6">
                        Tente ajustar os filtros para ver mais resultados.
                    </p>
                </div>
            ) : null}

            {/* Empty State */}
            {!isLoading && !error && funnels.length === 0 && (
                <div className="rounded-2xl bg-white p-12 shadow-sm border border-gray-100 text-center">
                    <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#7dd3fc]/20 to-[#38bdf8]/10 rounded-2xl flex items-center justify-center mb-4">
                        <svg
                            className="w-8 h-8 text-[#0f172a]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Nenhum funil cadastrado
                    </h3>
                    <p className="text-gray-600 mb-6">
                        Crie seu primeiro funil para começar a automatizar suas conversas.
                    </p>
                </div>
            )}

            {/* Modal de Criação */}
            <CreateFunnelModal
                open={isCreateModalOpen}
                onOpenChange={setIsCreateModalOpen}
                onSubmit={handleCreateFunnel}
                isLoading={createFunnelMutation.isPending}
            />

            {/* Modal de Edição */}
            <EditFunnelModal
                open={isEditModalOpen}
                onOpenChange={setIsEditModalOpen}
                funnel={selectedFunnel}
                onSubmit={handleEditSubmit}
                isLoading={updateFunnelMutation.isPending}
            />

            <DuplicateFunnelModal
                open={isDuplicateModalOpen}
                onOpenChange={setIsDuplicateModalOpen}
                funnel={selectedFunnel}
                onSubmit={(pageIds) => duplicateFunnelMutation.mutate(pageIds)}
                isLoading={duplicateFunnelMutation.isPending}
            />

            <ExportImportModal
                open={isExportImportOpen}
                onOpenChange={setIsExportImportOpen}
                exportFunnel={exportFunnelTarget}
                funnels={funnels}
            />

            {/* Confirm ativar/desativar */}
            <ConfirmDialog
                open={!!confirmToggle}
                onOpenChange={(v) => !v && setConfirmToggle(null)}
                title={confirmToggle?.is_active ? `Desativar funil "${confirmToggle?.nome}"?` : `Ativar funil "${confirmToggle?.nome}"?`}
                description={confirmToggle?.is_active ? 'O funil será pausado e deixará de processar novas conversas.' : 'O funil será ativado e começará a processar novas conversas.'}
                confirmLabel={confirmToggle?.is_active ? 'Desativar' : 'Ativar'}
                variant={confirmToggle?.is_active ? 'warning' : 'default'}
                isLoading={toggleActiveMutation.isPending}
                onConfirm={() => {
                    if (!confirmToggle) return;
                    setPendingActivateFunnel(confirmToggle);
                    setConfirmToggle(null);
                    toggleActiveMutation.mutate({ id: confirmToggle.id, isActive: confirmToggle.is_active });
                }}
            />

            {/* Confirm deletar */}
            <ConfirmDialog
                open={!!confirmDelete}
                onOpenChange={(v) => !v && setConfirmDelete(null)}
                title={`Excluir funil "${confirmDelete?.nome}"?`}
                description="Esta ação não pode ser desfeita. Todos os dados do funil serão perdidos."
                confirmLabel="Excluir"
                variant="danger"
                isLoading={deleteFunnelMutation.isPending}
                onConfirm={() => {
                    if (!confirmDelete) return;
                    const id = confirmDelete.id;
                    setConfirmDelete(null);
                    deleteFunnelMutation.mutate(id);
                }}
            />

            {/* Confirm ativar após corrigir variáveis */}
            <ConfirmDialog
                open={!!confirmAfterFixed}
                onOpenChange={(v) => { if (!v) { setConfirmAfterFixed(null); setPendingActivateFunnel(null); } }}
                title={`Ativar funil "${confirmAfterFixed?.nome}"?`}
                description="Variáveis corrigidas! O funil será ativado agora."
                confirmLabel="Ativar"
                variant="default"
                isLoading={toggleActiveMutation.isPending}
                onConfirm={() => {
                    if (!confirmAfterFixed) return;
                    const funnel = confirmAfterFixed;
                    setConfirmAfterFixed(null);
                    setPendingActivateFunnel(null);
                    toggleActiveMutation.mutate({ id: funnel.id, isActive: false });
                }}
            />

            {/* Modal de correção de variáveis ao ativar funil */}
            {variablesCopyOpen && pendingActivateFunnel && (
                <VariablesCopyDialog
                    open={variablesCopyOpen}
                    onOpenChange={setVariablesCopyOpen}
                    pages={pagesWithMissing}
                    selectedPages={(metaPagesData ?? []).map(p => ({
                        id: p.id,
                        page_id: p.page_id,
                        page_name: p.page_name ?? p.page_id,
                    } as Page))}
                    onFixed={() => {
                        setVariablesCopyOpen(false);
                        setConfirmAfterFixed(pendingActivateFunnel);
                        setPagesWithMissing([]);
                    }}
                />
            )}
        </div>
    );
}